import React, { useEffect, useState } from "react";
import { loader } from "graphql.macro";
import { useMutation } from "@apollo/client";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Modal,
  TextField,
  Typography,
} from "@mui/material";
import { FormattedDate, FormattedMessage, useIntl } from "react-intl";
import Plans, { PlanButton } from "../Plans/Plans";
import ManageBillingButton from "./ManageBillingButton";
import dialogStyles from "../../assets/stylesheets/dialog.module.scss";
import {
  ENTERPRISE_CONTACT,
  Interval,
  MAX_SEATS,
  PaidPlan,
  checkoutPath,
  intervalOf,
  paidPlanOf,
  planLookupKey,
} from "../../lib/plans";

const UPDATE_SUBSCRIPTION = loader(
  "../../schema/mutation/UpdateSubscription.graphql"
);

interface Props {
  organization: Organization;
  membersCount: number;
  // Only the owner can subscribe / manage billing; admins see the cards read-only.
  canManage: boolean;
}

// The subscription of the organization and the pricing table, on the
// organization page. Free: the owner subscribes with the Checkout
// (/billing/checkout). Starter or Pro Security: the owner changes the plan,
// Voice Trust+ and the seats here (the Stripe portal can't with two items), and
// the payment method, the invoices or the cancellation in the portal.
// Enterprise: managed by our team.
const Billing: React.FC<Props> = ({
  organization,
  membersCount,
  canManage,
}) => {
  const intl = useIntl();
  const [updateSubscription, { loading: updating }] =
    useMutation(UPDATE_SUBSCRIPTION);
  const [error, setError] = useState<string | null>(null);
  // annual -> monthly is scheduled at the end of the paid year
  const [scheduled, setScheduled] = useState(false);
  const [seats, setSeats] = useState(String(organization.numberOfSeats));
  // the webhook changed it, e.g. just after the Checkout
  useEffect(
    () => setSeats(String(organization.numberOfSeats)),
    [organization.numberOfSeats]
  );

  const checkout = new URLSearchParams(window.location.search).get("checkout");
  const currentPaidPlan = paidPlanOf(organization.plan);
  const currentInterval = intervalOf(organization.billingInterval);

  // the change waiting for the confirmation of the owner, see ConfirmChange
  const [pending, setPending] = useState<Change | null>(null);

  const change = (
    plan: PaidPlan,
    interval: Interval,
    addon: boolean,
    quantity: number
  ) => setPending({ plan, interval, addon, quantity });

  const apply = async ({ plan, interval, addon, quantity }: Change) => {
    setPending(null);
    setError(null);
    setScheduled(false);
    try {
      await updateSubscription({
        variables: {
          organizationId: organization.id,
          price: planLookupKey(plan, interval),
          addon,
          quantity,
        },
      });
      if (currentInterval === "yearly" && interval === "monthly") {
        setScheduled(true);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  const nothing = <></>;

  const renderAction = (
    column: string,
    { interval, addon }: { interval: Interval; addon: boolean }
  ) => {
    if (column === "enterprise") return undefined;
    if (!canManage || column === "free") return nothing;
    const plan = column as PaidPlan;
    if (organization.plan === "FREE") {
      return (
        <PlanButton
          primary={plan === "pro_security"}
          href={checkoutPath(plan, interval, addon, organization.id)}
        >
          <FormattedMessage id="plans.subscribe" defaultMessage="Subscribe" />
        </PlanButton>
      );
    }
    if (!currentPaidPlan) return nothing;
    const unchanged =
      plan === currentPaidPlan &&
      interval === currentInterval &&
      addon === organization.voiceTrustPlus;
    if (unchanged) return nothing;
    return (
      <PlanButton
        primary
        disabled={updating}
        onClick={() =>
          change(plan, interval, addon, organization.numberOfSeats)
        }
      >
        {plan === currentPaidPlan ? (
          <FormattedMessage
            id="billing.update-subscription"
            defaultMessage="Update my subscription"
          />
        ) : (
          <FormattedMessage
            id="billing.switch-to-plan"
            defaultMessage="Switch to {plan}"
            values={{
              plan: plan === "starter" ? "Starter" : "Pro Security",
            }}
          />
        )}
      </PlanButton>
    );
  };

  const seatsNumber = parseInt(seats, 10);
  const seatsValid =
    seatsNumber >= Math.max(1, membersCount) && seatsNumber <= MAX_SEATS;

  return (
    <Box>
      {checkout === "complete" && organization.plan === "FREE" && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <FormattedMessage
            id="billing.processing"
            defaultMessage="Your payment is being processed. Your new plan will appear here shortly."
          />
        </Alert>
      )}
      {organization.suspendedAt && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <FormattedMessage
            id="billing.suspended"
            defaultMessage="The payment of your subscription failed: the meetings of the organization are suspended, its history stays accessible. Update the payment method with the button below. Without a payment, the organization is back on the free plan on {date}."
            values={{
              date: organization.suspensionEndsAt ? (
                <FormattedDate
                  value={new Date(organization.suspensionEndsAt)}
                />
              ) : (
                "—"
              ),
            }}
          />
        </Alert>
      )}
      {checkout === "canceled" && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <FormattedMessage
            id="billing.canceled"
            defaultMessage="The payment was canceled, your plan didn't change."
          />
        </Alert>
      )}

      <Box
        sx={{
          border: "1px solid #EAE1F2",
          borderRadius: "12px",
          p: 3,
          mb: 4,
          display: "flex",
          flexDirection: "column",
          gap: 1,
        }}
      >
        <Typography sx={{ fontWeight: 700, fontSize: "1.1rem" }}>
          <FormattedMessage
            id="billing.your-plan"
            defaultMessage="Your plan: {plan}"
            values={{ plan: <PlanName organization={organization} /> }}
          />
        </Typography>

        {organization.plan === "FREE" &&
          typeof organization.freeDemosUsed === "number" && (
            <Typography>
              <FormattedMessage
                id="billing.free-demos-used"
                defaultMessage="{used} of {total} demos used this month: meetings of 15 minutes, 2 participants at most."
                values={{
                  used: organization.freeDemosUsed,
                  total: organization.freeDemosPerMonth,
                }}
              />
            </Typography>
          )}

        {organization.plan === "ENTERPRISE" && (
          <Typography>
            <FormattedMessage
              id="billing.enterprise"
              defaultMessage="Your plan is managed by our team, <a>contact us</a> to change it."
              values={{
                a: (...chunks: React.ReactNode[]) => (
                  <a href={ENTERPRISE_CONTACT}>{chunks}</a>
                ),
              }}
            />
          </Typography>
        )}

        {currentPaidPlan && (
          <>
            <Typography>
              {organization.billingInterval === "year" ? (
                <FormattedMessage
                  id="billing.billed-annually"
                  defaultMessage="Billed annually"
                />
              ) : (
                <FormattedMessage
                  id="billing.billed-monthly"
                  defaultMessage="Billed monthly"
                />
              )}
              {organization.subscriptionDate && (
                <>
                  {" · "}
                  <FormattedMessage
                    id="billing.since"
                    defaultMessage="since {date}"
                    values={{
                      date: (
                        <FormattedDate
                          value={new Date(organization.subscriptionDate)}
                        />
                      ),
                    }}
                  />
                </>
              )}
            </Typography>
            {organization.subscriptionCancelAt && (
              <Alert severity="warning">
                <FormattedMessage
                  id="billing.cancel-at"
                  defaultMessage="Your subscription ends on {date}, then you're back on the free plan."
                  values={{
                    date: (
                      <FormattedDate
                        value={new Date(organization.subscriptionCancelAt)}
                      />
                    ),
                  }}
                />
              </Alert>
            )}
            <Typography>
              <FormattedMessage
                id="billing.seats-used"
                defaultMessage="{used} of {total} seats used"
                values={{
                  used: membersCount,
                  total: organization.numberOfSeats,
                }}
              />
            </Typography>
            {canManage && (
              <Box
                sx={{
                  display: "flex",
                  gap: 2,
                  alignItems: "center",
                  flexWrap: "wrap",
                  mt: 1,
                }}
              >
                <TextField
                  type="number"
                  size="small"
                  label={intl.formatMessage({
                    id: "billing.seats",
                    defaultMessage: "Seats",
                  })}
                  value={seats}
                  onChange={(event) => setSeats(event.target.value)}
                  inputProps={{
                    min: Math.max(1, membersCount),
                    max: MAX_SEATS,
                  }}
                  sx={{ width: 120 }}
                />
                <Button
                  variant="outlined"
                  color="secondary"
                  disabled={
                    updating ||
                    !seatsValid ||
                    seatsNumber === organization.numberOfSeats
                  }
                  onClick={() =>
                    change(
                      currentPaidPlan,
                      currentInterval,
                      organization.voiceTrustPlus,
                      seatsNumber
                    )
                  }
                >
                  {updating ? (
                    <CircularProgress size={20} />
                  ) : (
                    <FormattedMessage
                      id="billing.update-seats"
                      defaultMessage="Change the number of seats"
                    />
                  )}
                </Button>
                <ManageBillingButton organizationId={organization.id} />
              </Box>
            )}
          </>
        )}
        {error && <Alert severity="error">{error}</Alert>}
        {scheduled && (
          <Alert severity="success">
            <FormattedMessage
              id="billing.change-scheduled"
              defaultMessage="Done: your plan becomes monthly at the end of the current year, it stays annual until then."
            />
          </Alert>
        )}
      </Box>

      {pending && (
        <ConfirmChange
          change={pending}
          scheduled={
            currentInterval === "yearly" && pending.interval === "monthly"
          }
          onCancel={() => setPending(null)}
          onConfirm={() => apply(pending)}
        />
      )}

      <Plans
        // the cards start from the subscription: again when it changes
        key={`${organization.plan}-${organization.voiceTrustPlus}-${organization.billingInterval}`}
        currentPlan={organization.plan}
        currentAddon={organization.voiceTrustPlus}
        initialInterval={currentInterval}
        renderAction={renderAction}
      />
    </Box>
  );
};

interface Change {
  plan: PaidPlan;
  interval: Interval;
  addon: boolean;
  quantity: number;
}

// Asks the owner to confirm a change of the subscription, like the other
// confirmations of the app (dialog.module.scss)
const ConfirmChange: React.FC<{
  change: Change;
  // annual -> monthly: at the end of the paid year
  scheduled: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}> = ({ change, scheduled, onCancel, onConfirm }) => (
  <Modal open onClose={onCancel}>
    <Box className={dialogStyles.dialog} sx={{ bgcolor: "background.paper" }}>
      <Typography className={dialogStyles.title}>
        <FormattedMessage
          id="billing.confirm-title"
          defaultMessage="Change your subscription?"
        />
      </Typography>
      <Typography className={dialogStyles.secondaryText}>
        <FormattedMessage
          id="billing.confirm-summary"
          defaultMessage="{plan}, {seats, plural, one {# seat} other {# seats}}, {interval, select, yearly {billed annually} other {billed monthly}}."
          values={{
            plan: (
              <PlanName
                organization={{
                  plan: change.plan === "starter" ? "STARTER" : "PRO_SECURITY",
                  voiceTrustPlus: change.addon,
                }}
              />
            ),
            seats: change.quantity,
            interval: change.interval,
          }}
        />
      </Typography>
      <Typography className={dialogStyles.secondaryText}>
        {scheduled ? (
          <FormattedMessage
            id="billing.confirm-scheduled"
            defaultMessage="Your plan stays annual until the end of the current year, then becomes monthly."
          />
        ) : (
          <FormattedMessage
            id="billing.confirm-now"
            defaultMessage="The change applies now, the difference is prorated on your next invoice."
          />
        )}
      </Typography>
      <Box className={dialogStyles.actions}>
        <Button
          onClick={onCancel}
          className={dialogStyles.cancelButton}
          variant="contained"
          color="secondary"
        >
          <FormattedMessage id="actions.back" defaultMessage="Back" />
        </Button>
        <Button
          className={dialogStyles.validateButton}
          onClick={onConfirm}
          variant="contained"
        >
          <FormattedMessage
            id="billing.confirm-change-button"
            defaultMessage="Change my subscription"
          />
        </Button>
      </Box>
    </Box>
  </Modal>
);

export const PlanName: React.FC<{
  organization: Pick<Organization, "plan" | "voiceTrustPlus">;
}> = ({ organization }) => {
  switch (organization.plan) {
    case "FREE":
      return (
        <FormattedMessage id="plans.free.title" defaultMessage="Free demo" />
      );
    case "ENTERPRISE":
      return <>Enterprise Fortress</>;
    default:
      return (
        <>
          {organization.plan === "STARTER" ? "Starter" : "Pro Security"}
          {organization.voiceTrustPlus && " + Voice Trust+"}
        </>
      );
  }
};

export default Billing;
